import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getDashboardTableSoldSelector, getDashboardTablesSold} from '@/store/organization-dashboard';
//mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
//constants
import {
  DOLLAR,
  DOWNLOAD_CSV,
  HASH,
  PAY_STATUS_CAMEL,
  PRICE,
  RIGHT,
  TABLE,
  TABLE_NAME,
  TYPE_OF_SEAT,
  TABLE_PURCHASER,
  defaultPage,
  defaultPageSize,
  PAY_STATUS,
} from '@/utils/constants';
//helpers
import {downloadFile, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
//services
import {OrganizationDashboardService} from '@/utils/services';
//enums
import {STATISTIC} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticTablesSoldStyle} from './StatisticTablesSold.styles';

const StatisticTablesSold = () => {
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [searchName, setSearchName] = useState('');

  const [sortColumn, setSortColumn] = useState<string>();
  const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const tableSold = useAppSelector(getDashboardTableSoldSelector);

  const {data, pageSize, totalCount} = tableSold;

  useEffect(() => {
    if (currentEvent) getTablesSold(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc]);

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
    setIsOrderDesc(isAsc);
    setSortColumn(newSortColumn);
  };

  const getTablesSold = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: currentPage,
        sortColumn,
        isOrderDesc,
        searchName: value,
      };

      await dispatch(getDashboardTablesSold(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handleDownloadSvc = async () => {
    if (currentEvent) {
      const file = await OrganizationDashboardService.getDashboardTablesSoldCsv(currentEvent.id);
      downloadFile(file.data, STATISTIC.TABLES_SOLD);
    }
  };

  const handleSearchChange = (value: string) => {
    setCurrentPage(defaultPage);
    setSearchName(value);
  };

  const handlePaginationChange = (step: number) => {
    if (isOrderDesc) setIsOrderDesc(false);
    if (sortColumn?.length) setSortColumn('');

    setCurrentPage(step);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
  };

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  useEffect(() => {
    if (currentEvent) getTablesSold(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc, searchName]);

  return (
    <Box sx={statisticTablesSoldStyle.wrapper}>
      <SearchHeader
        page={currentPage}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        actionButton={
          <Button
            onClick={handleDownloadSvc}
            title={DOWNLOAD_CSV}
            disabled={!totalCount}
            startIcon={<ImportFileIcon />}
            sx={statisticTablesSoldStyle.searchButton}
          />
        }
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={statisticTablesSoldStyle.tableContent}>
            <TableContainer sx={statisticTablesSoldStyle.container}>
              <Table sx={statisticTablesSoldStyle.table}>
                <TableHead>
                  <TableRow sx={statisticTablesSoldStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange('Number')} IconComponent={UpDownIcon}>
                        {TABLE}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{TABLE_NAME}</TableCell>

                    <TableCell>{TYPE_OF_SEAT}</TableCell>

                    <TableCell>{TABLE_PURCHASER}</TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(PRICE)} IconComponent={UpDownIcon}>
                        {PRICE}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(PAY_STATUS_CAMEL)} IconComponent={UpDownIcon}>
                        {PAY_STATUS}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id} sx={statisticTablesSoldStyle.tableBody}>
                      <TableCell>
                        {HASH}
                        {row.number}
                      </TableCell>

                      <TableCell>{row.name}</TableCell>

                      <TableCell>{row.typeOfSeat}</TableCell>

                      <TableCell>{row.purchaser}</TableCell>

                      <TableCell align={RIGHT}>
                        {DOLLAR}
                        {row.price}
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Typography sx={getPayStatusSx(row.payStatus)}>
                          {row.payStatus}
                          <EllipseIcon />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={statisticTablesSoldStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => handlePaginationChange(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData />
        )
      ) : (
        <Loader parentClassName={statisticTablesSoldStyle.loader} size={50} />
      )}
    </Box>
  );
};

export default StatisticTablesSold;
